// import { IOptionalTimeWindow } from '../../types/datetimes';
// import { getEffectiveVisitDate } from '../queues/helpers';
import { NORTH_STAR_DEATH_VISIT_FILTER } from '@/pages/DashboardPage/containers/variations/northStar';
import { gql, useQuery } from '@apollo/client';
import { Tooltip } from '@mui/material';
import moment from 'moment';

import { TagChip } from '../tags/TagChip';

enum NorthStarPatientStatus {
    DISCHARGED = 'discharge',
    DECEASED = 'death',
}

const PATIENT_NORTH_STAR_PATIENT_STATUS_QUERY = gql`
    query PatientNorthStarPatientStatus($patientId: UUID!, $visitFilter: VisitFilter!) {
        patient(id: $patientId) {
            id
            metadata
            deathVisits: visits(
                filters: $visitFilter
                ordering: { effectiveDate: DESC }
                pagination: { limit: 1, offset: 0 }
            ) {
                totalCount
                items {
                    effectiveDate
                }
            }
        }
    }
`;

interface IMetadataChart {
    startDate: string | null;
    dischargeDate: string | null;
}

const isMetadataChart = (value: unknown): value is IMetadataChart => {
    return (
        typeof value === 'object' &&
        value !== null &&
        'startDate' in value &&
        'dischargeDate' in value
    );
};

interface INorthStarDeathVisit {
    effectiveDate: Date;
}

interface IPatientNorthStarPatientStatusQueryResult {
    patient: {
        metadata: {
            isDischarged?: boolean | null;
            isDeceased?: boolean | null;
            charts?: IMetadataChart[] | null;
        } | null;
        deathVisits: {
            totalCount: number;
            items: INorthStarDeathVisit[];
        } | null;
    } | null;
}

const useNorthStarPatientStatus = (
    patientId: string,
): { status: NorthStarPatientStatus; date: Date | null } | null => {
    const { data } = useQuery<IPatientNorthStarPatientStatusQueryResult>(
        PATIENT_NORTH_STAR_PATIENT_STATUS_QUERY,
        {
            variables: { patientId, visitFilter: NORTH_STAR_DEATH_VISIT_FILTER },
        },
    );
    if (!data?.patient) {
        return null;
    }
    if (data.patient.deathVisits?.totalCount && data.patient.deathVisits.totalCount > 0) {
        return {
            status: NorthStarPatientStatus.DECEASED,
            date: data.patient.deathVisits.items[0].effectiveDate,
        };
    }
    if (data.patient.metadata?.isDischarged) {
        if (!data.patient.metadata.charts) {
            return { status: NorthStarPatientStatus.DISCHARGED, date: null };
        }
        const dischargeDate = [...data.patient.metadata.charts]
            .filter(isMetadataChart)
            .sort((a, b) => {
                if (!a.dischargeDate && !b.dischargeDate) return 0;
                if (!a.dischargeDate) return 1;
                if (!b.dischargeDate) return -1;
                return new Date(a.dischargeDate).getTime() - new Date(b.dischargeDate).getTime();
            })[0]?.dischargeDate;
        if (!dischargeDate) {
            return { status: NorthStarPatientStatus.DISCHARGED, date: null };
        }
        return { status: NorthStarPatientStatus.DISCHARGED, date: new Date(dischargeDate) };
    }
    return null;
};

export const NorthStarPatientStatusTag = ({ patientId }: { patientId: string }) => {
    const patientNorthStarPatientStatus = useNorthStarPatientStatus(patientId);
    if (patientNorthStarPatientStatus === null) {
        return null;
    }
    const { status, date } = patientNorthStarPatientStatus;
    const dateLabel = date ? moment(date).format('MMM D, YYYY') : '';

    const sx = {
        backgroundColor: '#1C3937',
        color: '#FFFFFF',
    };

    switch (status) {
        case NorthStarPatientStatus.DISCHARGED:
            return (
                <Tooltip title={`Discharged ${dateLabel}`.trim()}>
                    <TagChip label='Discharged' size='small' sx={sx} />
                </Tooltip>
            );
        case NorthStarPatientStatus.DECEASED:
            return (
                <Tooltip title={`Deceased ${dateLabel}`.trim()}>
                    <TagChip label='Deceased' size='small' sx={sx} />
                </Tooltip>
            );
    }
};
